import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StoreService } from '@core/services/core/storage/store.service';
import { GuidedLoginHelperService } from '@core/services/helpers/guided-login-helper.service';
import { from, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { User, UserLoginStatus } from '../../models/user';
import { padZeros } from '../../utils/utils';
import { AuthType } from '../core/auth/auth.actions';
import { BaseService, HttpStatusCode } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class AuthHttpService {

  readonly LOGIN_STORAGE_KEY = 'mets_login_data';
  private token: string;

  constructor(
    private http: HttpClient,
    private mmobileAux: MMobileHelperService,
    private base: BaseService,
    private or: OperationRegisterService,
    private storage: StoreService,
    private guidedLoginHelper: GuidedLoginHelperService
  ) {
    this.populateStoredLoginData();
  }

  login({ documentNumber, password }, type: AuthType) {
    const url = this.mmobileAux.getJWTOIMLoginUrl();
    const body = { username: padZeros(documentNumber), password };
    const guidedLoginParams = this.guidedLoginHelper.getStoredGuidedLoginParams();
    const guidedLoginService = this.guidedLoginHelper.getGuidedLoginServiceById(guidedLoginParams?.serviceId);

    return this.http
      .post<any>(url, body, {
        headers: this.base.getLoginHeaders(),
        params: this.or.setLoginOperationParams(type, documentNumber, guidedLoginService?.name),
        observe: 'response'
      })
      .pipe(map((response: HttpResponse<User>) => this.mapLogin(response)));
  }

  loginSdm({ documentNumber, password }, type: AuthType) {
    const url = `${this.base.getSdmApiAuthBaseUrl()}/login`;
    const body = { document: padZeros(documentNumber), password };

    return this.http
      .post<any>(url, body, {
        headers: this.base.getSdmApiAuthHeaders(this.or.getOrigin().toString(), type.toString()),
        observe: 'response'
      }).pipe(
        switchMap(res => {
          if (res?.body?.temporal_token) {
            return throwError({
              error: {error: { error_detail: { status: UserLoginStatus.ACTIVE_BUT_BLOCKED }}},
              status: HttpStatusCode.FORBIDDEN
            });
          } else {
            return from(this.storeLoginData(res)).pipe(switchMap(() => of(res)));
          }
        })
      );
  }

  loginSim({ documentNumber, password, nuumaSim }) {
    const url = `${this.mmobileAux.getJWTOIMSimLoginUrl()}?documentNumber=${padZeros(
      documentNumber
    )}`;
    const headers = this.base
      .getLoginHeaders()
      .append('Authorization', 'Basic ' + btoa(`${nuumaSim}:${password}`));

    return this.http
      .get<any>(url, { headers, observe: 'response' })
      .pipe(map((response: HttpResponse<User>) => this.mapLogin(response)));
  }

  businessLogin(body: { cif: string; nif: string; password: string }) {
    const url = `${
      this.base.getAwsBaseUrl()
    }/metsintegration/login`;
    return this.http.post<User>(url, body);
  }

  getLoginInfo() {
    return from(this.mmobileAux.isReady()).pipe(switchMap(() => {
      const url = `${this.base.getPrivateOIMBaseUrl()}/loginInfo`;
      const { headers } = this.base.getPrivateOIMHeaders({ cacheResponse: true });
      return this.http.get<any>(url, { headers, observe: 'response' } )
      .pipe(map((response: HttpResponse<User>) => this.mapLogin(response)));
    }));
  }

  getSdmUserProfile() {
    const url = `${this.base.getSdmApiMetsBaseUrl()}/profile`;
    const headers = this.base.getSdmPrivateHeaders(this.token);
    return this.http.get<any>(url, { headers, observe: 'response' } )
    .pipe(map((response: HttpResponse<any>) => this.mapSdmUser(response)));
  }

  getCompaniesLoginInfo(token: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/loginsim`;
    const headers = new HttpHeaders().append('Content-Type', 'application/json').append('Authorization', token);
    return this.http.post<User>(url, {}, {headers});
  }

  aciLogin(body: { username: string; password: string}){
    return from(this.mmobileAux.isReady()).pipe(switchMap(() => {
      let url = this.mmobileAux.getJWTOIMAciLoginUrl();
      url += this.or.getAciLoginROQueryParams(body.username);
      return this.http.post<any>(url, body, {headers: this.base.getLoginHeaders(), observe: 'response' })
     .pipe(map((response: HttpResponse<User>) => this.mapLogin(response)));
  }));
  }

  businessCertificateLogin(token: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/loginCert`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post<User>(url, {}, {headers});
  }

  private mapLogin(response: HttpResponse<User>) {
    return {
      ...response.body,
      tokenPass: response.body.token,
      token: response.headers.get('MMOBILE_JWT_ACCESS_TOKEN'),
      refreshToken: response.headers.get('MMOBILE_JWT_REFRESH_TOKEN'),
      policyAccepted: response.body.policyAccepted || false
    };
  }

  private mapSdmUser(response: HttpResponse<any>) {
    const body = response?.body;
    const address = body?.address;
    return {
      name: body?.name,
      surname1: body?.last_name_1,
      surname2: body?.last_name_2,
      status: body?.status,
      documentType: body?.document_type_desc,
      documentNumber: body?.document_number,
      phone: body?.telephone_1,
      secondPhone: body?.telephone_2,
      email: body?.email,
      birthdate: body?.birthdate,
      address: {
        additionalInfo: address?.complement_address,
        city: address?.city,
        door: address?.door,
        floor: address?.floor,
        number: address?.road_number,
        portal: address?.portal,
        postalCode: address?.postal_code,
        province: address?.state,
        stair: address?.stairs,
        street: address?.road_name,
        streetType: address?.road_type_code,
      },
      userCode: body?.user_code,
      tokenPass: body?.token,
      policyAccepted: body?.accepted_policy || false
    };
  }

  getUserNetwork(user: User) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/questions?type=BELONGS_TO_INTERNAL_NETWORK`;
    const params = this.or.registerNetworkOperationParams(user);

    return this.http.get<{response: boolean}>(url, {
      ...this.base.getPrivateOIMHeaders(),
      params
    }).pipe(
      map( data => data.response )
    );
  }

  refreshAccessToken(jwtRefreshToken: string) {
    const url = this.mmobileAux.getJWTOIMLoginRefreshUrl();
    const headers = this.base
      .getLoginHeaders()
      .append('X-Authorization', `Bearer ${jwtRefreshToken}`);

    return this.http.get<{ token: string }>(url, { headers });
  }

  private storeLoginData(res: HttpResponse<any>) {
    this.token = res.body.token;
    return this.storage.save(this.LOGIN_STORAGE_KEY, res.body);
  }

  private async populateStoredLoginData() {
    const loginData = await this.storage.get(this.LOGIN_STORAGE_KEY) || '{}';
    this.token = loginData?.token;
  }
}
